import { parse as parseYaml } from 'yaml';
import { SKILL_FILE_NAME, SKILL_LIMITS, SKILL_NAME_PATTERN } from './limits.ts';

export interface SkillFile {
  readonly path: string;
  readonly content: string;
}

export interface ParsedSkill {
  readonly name: string;
  readonly description: string;
  /** SKILL.md after its frontmatter: what the model reads when it loads the skill. */
  readonly instructions: string;
  /** Every file, SKILL.md included, sorted by path. */
  readonly files: readonly SkillFile[];
}

export type SkillParseResult =
  | { readonly ok: true; readonly skill: ParsedSkill }
  | { readonly ok: false; readonly problems: readonly string[] };

const FRONTMATTER = /^﻿?---\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/;
const KNOWN_KEYS = new Set([
  'name',
  'description',
  'license',
  'compatibility',
  'metadata',
  'allowed-tools',
]);
const PATH_SEGMENT = /^[A-Za-z0-9_][A-Za-z0-9._-]*$/;

/**
 * Checks a skill folder in the Agent Skills format: a SKILL.md at its root whose frontmatter has
 * `name` and `description`, plus any other UTF-8 text files. The problems are sentences for the
 * person or the model that wrote the skill.
 */
export function parseSkillFiles(files: readonly SkillFile[]): SkillParseResult {
  const problems = fileProblems(files);
  if (problems.length > 0) return { ok: false, problems };
  const skillMd = files.find((file) => file.path === SKILL_FILE_NAME);
  if (!skillMd)
    return { ok: false, problems: [`the folder has no ${SKILL_FILE_NAME} at its root`] };
  const front = readFrontmatter(skillMd.content);
  if (!front.ok) return front;
  const { fields, instructions } = front;
  problems.push(...frontmatterProblems(fields));
  if (instructions.trim() === '') {
    problems.push(`${SKILL_FILE_NAME} has no instructions after its frontmatter`);
  }
  if (problems.length > 0) return { ok: false, problems };
  return {
    ok: true,
    skill: {
      name: fields.name as string,
      description: (fields.description as string).trim(),
      instructions: instructions.trim(),
      files: [...files].sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0)),
    },
  };
}

function fileProblems(files: readonly SkillFile[]): string[] {
  const L = SKILL_LIMITS;
  if (files.length > L.maxFiles) return [`a skill has at most ${L.maxFiles} files`];
  const problems: string[] = [];
  const seen = new Set<string>();
  let total = 0;
  for (const file of files) {
    const pathProblem = skillPathProblem(file.path);
    if (pathProblem) problems.push(pathProblem);
    if (seen.has(file.path)) problems.push(`${file.path}: appears twice`);
    seen.add(file.path);
    const bytes = new TextEncoder().encode(file.content).byteLength;
    total += bytes;
    if (bytes > L.maxFileBytes)
      problems.push(`${file.path}: larger than ${L.maxFileBytes / 1024} KB`);
    if (file.content.includes('\u0000')) problems.push(`${file.path}: not a text file`);
  }
  if (total > L.maxTotalBytes) {
    problems.push(`the files add up to more than ${L.maxTotalBytes / 1024 / 1024} MB`);
  }
  return problems.slice(0, 8);
}

/** A file's path inside a skill: relative, forward slashes, no hidden or parent segments. */
export function skillPathProblem(path: string): string | null {
  if (path.length === 0 || path.length > SKILL_LIMITS.maxPathChars) {
    return `${path || '(empty path)'}: a path has 1 to ${SKILL_LIMITS.maxPathChars} characters`;
  }
  const segments = path.split('/');
  if (segments.length > SKILL_LIMITS.maxPathDepth) {
    return `${path}: nested more than ${SKILL_LIMITS.maxPathDepth} folders deep`;
  }
  if (!segments.every((segment) => PATH_SEGMENT.test(segment))) {
    return `${path}: use letters, digits, ".", "_" and "-" in names, and no hidden files`;
  }
  return null;
}

type Frontmatter =
  | { readonly ok: true; readonly fields: Record<string, unknown>; readonly instructions: string }
  | { readonly ok: false; readonly problems: readonly string[] };

function readFrontmatter(text: string): Frontmatter {
  const match = FRONTMATTER.exec(text);
  if (!match) {
    return {
      ok: false,
      problems: [`${SKILL_FILE_NAME} must start with frontmatter between "---" lines`],
    };
  }
  let fields: unknown;
  try {
    fields = parseYaml(match[1] ?? '');
  } catch {
    return { ok: false, problems: [`${SKILL_FILE_NAME}: its frontmatter is not valid YAML`] };
  }
  if (fields === null || typeof fields !== 'object' || Array.isArray(fields)) {
    return { ok: false, problems: [`${SKILL_FILE_NAME}: its frontmatter must be a set of keys`] };
  }
  return {
    ok: true,
    fields: fields as Record<string, unknown>,
    instructions: text.slice(match[0].length),
  };
}

function frontmatterProblems(fields: Record<string, unknown>): string[] {
  const L = SKILL_LIMITS;
  const problems: string[] = [];
  const unknown = Object.keys(fields).filter((key) => !KNOWN_KEYS.has(key));
  if (unknown.length > 0) {
    problems.push(
      `${SKILL_FILE_NAME}: unknown frontmatter keys ${unknown.map((key) => `"${key}"`).join(', ')}; ` +
        'put extra fields under "metadata"',
    );
  }
  const { name, description, compatibility, metadata } = fields;
  if (typeof name !== 'string' || name.length > L.nameChars || !SKILL_NAME_PATTERN.test(name)) {
    problems.push(
      `"name" must be 1 to ${L.nameChars} lower-case letters, digits and single hyphens, ` +
        'not starting or ending with a hyphen',
    );
  }
  if (
    typeof description !== 'string' ||
    description.trim() === '' ||
    description.length > L.descriptionChars
  ) {
    problems.push(`"description" must be 1 to ${L.descriptionChars} characters`);
  } else if (/[<>]/.test(description)) {
    problems.push('"description" may not contain "<" or ">"');
  }
  if (
    compatibility !== undefined &&
    (typeof compatibility !== 'string' || compatibility.length > L.compatibilityChars)
  ) {
    problems.push(`"compatibility" must be text of at most ${L.compatibilityChars} characters`);
  }
  if (
    metadata !== undefined &&
    (metadata === null ||
      typeof metadata !== 'object' ||
      Array.isArray(metadata) ||
      !Object.values(metadata).every((value) => typeof value === 'string'))
  ) {
    problems.push('"metadata" must map names to text');
  }
  for (const key of ['license', 'allowed-tools'] as const) {
    if (fields[key] !== undefined && typeof fields[key] !== 'string') {
      problems.push(`"${key}" must be text`);
    }
  }
  return problems;
}
